// @ts-nocheck

import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import extend from 'lodash/extend';

// Actions
import AuthActions from 'app/shared/flux/actions/AuthActions';

// Misc / Utils
import { incrementPis } from 'app/client/utils/browserCookieUtils';
import Area from 'app/shared/models/Area';
import api from 'app/shared/utils/api';
// todo: move this to a shared util
import clusterSettingCache from 'app/server/utils/clusterSettingCache';
import constants from 'app/shared/constants/ConstantsBundle';
import mapConstants from 'app/shared/constants/MapConstants';
import { emitTrackGoogleLoginEvent } from 'app/shared/models/Clickstream/LoginClickstreamEvents';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import { getPageName } from 'app/shared/utils/getPageName';
import { createGoogleSignIn, decodeGoogleCredential, waitForGoogleApi } from 'app/shared/utils/googleSignInUtils';
import { getLsKey } from 'app/shared/utils/debugUtils';

const logger = getGlobalLogger('actions/app');

const SIGN_IN_WITH_GOOGLE_ERROR_MESSAGES = {
  ACCOUNT_DEACTIVATED: constants.SIGN_IN_WITH_GOOGLE_ACCOUNT_DEACTIVATED,
  default: constants.SIGN_IN_WITH_GOOGLE_DEFAULT,
  EMAIL_NOT_SUPPLIED: constants.SIGN_IN_WITH_GOOGLE_EMAIL_NOT_SUPPLIED,
  INTERNAL_ERROR: constants.SIGN_IN_WITH_GOOGLE_INTERNAL_ERROR,
  INVALID_API_CALL: constants.SIGN_IN_WITH_GOOGLE_INVALID_API_CALL,
  INVALID_CREDENTIALS: constants.SIGN_IN_WITH_GOOGLE_INVALID_CREDENTIALS,
  USER_ALREADY_EXISTS: constants.SIGN_IN_WITH_GOOGLE_USER_ALREADY_EXISTS,
};

/**
 * Helper function to determine if Google One Tap prompt should be shown
 * @param {object} state - Redux state
 * @returns {boolean} - Whether to show the prompt
 */
const shouldShowOneTapPrompt = (state) => {
  const debug = getLsKey('debugGoogle') === 'true';

  if (typeof window === 'undefined') {
    if (debug) console.log('[Google OneTap] Server-side render, skipping');
    return false;
  }

  const pathname = window.location.pathname;
  const { loggedIn, hasOptedOutAdTracking } = state.user || {};

  if (debug) {
    console.log('[Google OneTap] Checking conditions:', {
      pathname,
      loggedIn,
      hasOptedOutAdTracking,
    });
  }

  // Don't show if already logged in
  if (loggedIn) {
    if (debug) console.log('[Google OneTap] Blocked: User is logged in');
    return false;
  }

  // Don't show if user opted out of tracking
  if (hasOptedOutAdTracking) {
    if (debug) console.log('[Google OneTap] Blocked: User opted out of ad tracking');
    return false;
  }

  // Only show on specific pages
  const isHomepage = pathname === '/';
  const isSearchPage = pathname.includes('/search') || pathname.match(/\/[a-z]{2}-[a-z]+\//); // e.g., /ca-san-francisco/
  const isListingPage = pathname.match(/\/pad\/\d+/); // e.g., /pad/123456

  if (debug) {
    console.log('[Google OneTap] Page checks:', {
      isHomepage,
      isSearchPage,
      isListingPage,
    });
  }

  if (!isHomepage && !isSearchPage && !isListingPage) {
    if (debug) console.log('[Google OneTap] Blocked: Not on allowed page');
    return false;
  }

  // Check dismissal cooldown (24 hours)
  try {
    const lastDismissed = localStorage.getItem('google_onetap_dismissed');

    if (lastDismissed) {
      const hoursSinceDismiss = (Date.now() - parseInt(lastDismissed, 10)) / (1000 * 60 * 60);
      if (debug) {
        console.log('[Google OneTap] Dismissal check:', {
          lastDismissed,
          hoursSinceDismiss: hoursSinceDismiss.toFixed(2),
          cooldownRemaining: (24 - hoursSinceDismiss).toFixed(2),
        });
      }
      if (hoursSinceDismiss < 24) {
        if (debug)
          console.log(`[Google OneTap] Blocked: Dismissed ${hoursSinceDismiss.toFixed(2)} hours ago (need 24h)`);
        return false;
      }
    }
  } catch (e) {
    // localStorage access can fail in private browsing mode or when storage is disabled
    if (debug) console.log('[Google OneTap] Could not access localStorage:', e);
  }

  if (debug) console.log('[Google OneTap] ✅ All checks passed, prompt should show');
  return true;
};

const AppActions = {
  sendEventToApi(type, id) {
    return (dispatch, getState) => {
      const state = getState();

      const apiArgs = {
        type,
        id,
      };

      const requiresId =
        type === 'previewed' ||
        type === 'addedToAccountFavorites' ||
        type === constants.LISTING_VIEWED ||
        type === constants.EXPIRED_LISTING_VIEWED;

      if (requiresId && !id) {
        logger?.warn(`AppActions#sendEventToApi called with type ${type} requires an id, but no id was passed`);
        return false;
      }
      if (state.analytics.ref) {
        extend(apiArgs, {
          ref: state.analytics.ref,
        });
      }
      return dispatch(api.event.trigger(apiArgs));
    };
  },
  refTrack(ref) {
    return function (dispatch) {
      if (ref) {
        if (isArray(ref)) {
          ref = ref[0];
        }

        dispatch(api.event.refTrack(ref));
      }
    };
  },
  pageOnLoad(pathname, query) {
    return function (dispatch, getState) {
      // happens on ever page change / client-side navigation page load
      if (window.COMSCORE) {
        const CS_FPID = getState()?.user?.userToken;
        const CS_FPIT = getState()?.user?.loggedIn === true ? 'li' : 'lo';
        window.COMSCORE.beacon({
          c1: '2',
          c2: '6036206',
          cs_fpid: `${CS_FPID}`,
          cs_fpit: `${CS_FPIT}`,
          cs_fpdm: '*null',
          cs_fpdt: '*null',
        });
      }
      if (!getState().app.isInitialSsrPage && window.COMSCORE) {
        dispatch(api.comscore.pageview());
      }
      dispatch(AppActions.updatePageDetails(pathname, query));
    };
  },
  updatePageDetails(pathname, query) {
    return function (dispatch, getState) {
      const areaType = getState().area.area.type;
      const type = constants.UPDATE_CURRENT_PAGE_DETAILS;
      const name = getPageName({ pathname, areaType });
      const currentPage = {
        name,
        pathname,
        query,
      };

      dispatch({
        type,
        currentPage,
      });

      incrementPis();
    };
  },
  loadSignInWithGoogleApi({ onError, onSuccess, skipAutoPrompt = false }) {
    return function (dispatch, getState) {
      if (typeof window !== 'undefined' && !window.google?.accounts?.id) {
        window.onGoogleLibraryLoad = () => {
          dispatch({
            type: constants.SIGN_IN_WITH_GOOGLE_INIT_SUCCESS,
          });
          if (onSuccess) {
            onSuccess();
          }
        };

        const googleLoginCallback = async () => {
          const debug = getLsKey('debugGoogle') === 'true';

          if (debug) console.log('[Google OneTap] Starting initialization...');

          // Wait for Google API to be fully loaded
          await waitForGoogleApi();

          if (debug) console.log('[Google OneTap] Google API loaded');

          // Create the Google Sign In instance with unified configuration
          const state = getState();
          const shouldAutoPrompt = !skipAutoPrompt && shouldShowOneTapPrompt(state);

          if (debug) {
            console.log('[Google OneTap] Configuration:', {
              skipAutoPrompt,
              shouldAutoPrompt,
              origin: window.location.origin,
              pathname: window.location.pathname,
            });
          }

          const googleSignIn = createGoogleSignIn({
            clientId: '353588935388-gtdkbktgahlhkisd7nas72b5p6vrno4u.apps.googleusercontent.com',
            onCredentialResponse: (res) => {
              const { credential } = res;
              emitTrackGoogleLoginEvent();

              // Decode credential to extract email for error handling
              const decodedUser = decodeGoogleCredential(credential);
              const userEmail = decodedUser?.email;

              if (debug) {
                console.log('[Google OneTap] Credential response received:', {
                  email: userEmail,
                  credentialLength: credential?.length,
                });
              }

              if (debug) console.log('[Google One Tap] Sign-in attempt for email:', userEmail);

              dispatch(AuthActions.login.signInWithGoogle(credential))
                .then(() => {
                  if (debug) console.log('[Google One Tap] Sign-in successful');
                  if (debug) console.log('[Google OneTap] ✅ Authentication complete, checking redirect');
                  dispatch(AuthActions.login.checkLoginPageRedirect());
                })
                .catch((err) => {
                  if (debug) console.log('[Google One Tap] Sign-in failed with error:', err);

                  if (debug) {
                    console.log('[Google OneTap] ❌ Sign-in error details:', {
                      error: err,
                      errorType: typeof err,
                      isString: isString(err),
                    });
                  }

                  if (isString(err)) {
                    const errorMsg =
                      SIGN_IN_WITH_GOOGLE_ERROR_MESSAGES[err] || SIGN_IN_WITH_GOOGLE_ERROR_MESSAGES.default;

                    // If user already exists, pass email to enable auto-transition to password
                    if (err === 'USER_ALREADY_EXISTS' && userEmail && onError) {
                      if (debug)
                        console.log(
                          '[Google One Tap] USER_ALREADY_EXISTS - passing email to error handler:',
                          userEmail,
                        );
                      if (debug) console.log('[Google OneTap] Auto-transitioning to password flow');
                      onError(errorMsg, userEmail);
                    } else if (onError) {
                      if (debug) console.log('[Google OneTap] Calling onError handler:', errorMsg);
                      onError(errorMsg);
                    }
                  }
                  logError({
                    error: err,
                    errorType: 'GoogleSignInError',
                    context: 'Error signing in with Sign In With Google api',
                  });
                });
            },
            onError: onError,
            onSuccess: onSuccess,
            autoPrompt: shouldAutoPrompt,
          });

          logger?.info(`Google One Tap autoPrompt: ${shouldAutoPrompt}`);

          if (debug) console.log('[Google OneTap] createGoogleSignIn returned, button rendering...');

          // Try to render button if container exists
          const buttonContainer = document.getElementById('g_id_signin');
          if (buttonContainer) {
            googleSignIn.renderButton('g_id_signin');
          }
        };

        try {
          const debug = getLsKey('debugGoogle') === 'true';

          if (debug) {
            console.log('[Google OneTap] Loading Google Sign-In script from accounts.google.com/gsi/client');
          }

          // Load Google Sign-In script directly
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.onload = () => {
            if (debug) console.log('[Google OneTap] ✅ Script loaded successfully');
            googleLoginCallback();
          };
          script.onerror = (error) => {
            if (debug) console.error('[Google OneTap] ❌ Script failed to load:', error);
            logger?.error('Failed to load Google Sign-In script', error);
          };
          document.head.appendChild(script);

          if (debug) console.log('[Google OneTap] Script tag appended to <head>');
        } catch (err) {
          const debug = getLsKey('debugGoogle') === 'true';
          if (debug) console.error('[Google OneTap] ❌ Exception while loading script:', err);
          logError({ error: err, errorType: 'GoogleSignInError', context: 'Error loading Sign In With Google api' });
        }
      } else if (typeof window !== 'undefined' && window.google?.accounts?.id && onSuccess) {
        // Google is already loaded, create instance immediately
        const state = getState();
        const shouldAutoPrompt = !skipAutoPrompt && shouldShowOneTapPrompt(state);

        const googleSignIn = createGoogleSignIn({
          clientId: '353588935388-gtdkbktgahlhkisd7nas72b5p6vrno4u.apps.googleusercontent.com',
          onCredentialResponse: (res) => {
            const { credential } = res;
            emitTrackGoogleLoginEvent();

            // Decode credential to extract email for error handling
            const decodedUser = decodeGoogleCredential(credential);
            const userEmail = decodedUser?.email;
            const debug = getLsKey('debugGoogle') === 'true';

            if (debug) console.log('[Google One Tap] Sign-in attempt for email:', userEmail);

            dispatch(AuthActions.login.signInWithGoogle(credential))
              .then(() => {
                if (debug) console.log('[Google One Tap] Sign-in successful');
                dispatch(AuthActions.login.checkLoginPageRedirect());
              })
              .catch((err) => {
                if (debug) console.log('[Google One Tap] Sign-in failed with error:', err);

                if (isString(err)) {
                  const errorMsg =
                    SIGN_IN_WITH_GOOGLE_ERROR_MESSAGES[err] || SIGN_IN_WITH_GOOGLE_ERROR_MESSAGES.default;

                  // If user already exists, pass email to enable auto-transition to password
                  if (err === 'USER_ALREADY_EXISTS' && userEmail && onError) {
                    if (debug)
                      console.log('[Google One Tap] USER_ALREADY_EXISTS - passing email to error handler:', userEmail);
                    onError(errorMsg, userEmail);
                  } else if (onError) {
                    onError(errorMsg);
                  }
                }
                logError({
                  error: err,
                  errorType: 'GoogleSignInError',
                  context: 'Error signing in with Sign In With Google api',
                });
              });
          },
          onError: onError,
          onSuccess: onSuccess,
          autoPrompt: shouldAutoPrompt,
        });

        logger?.info(`Google One Tap autoPrompt (already loaded): ${shouldAutoPrompt}`);

        // Try to render button if container exists
        const buttonContainer = document.getElementById('g_id_signin');
        if (buttonContainer) {
          googleSignIn.renderButton('g_id_signin');
        }
      }
    };
  },
  gmapApiSuccess() {
    return function (dispatch) {
      dispatch({
        type: constants.GMAP_API_READY,
      });
    };
  },
  toggleNotificationMessage(payload) {
    return function (dispatch) {
      dispatch({
        type: constants.NOTIFICATION_DISPLAY_BOOL,
        payload,
      });
    };
  },
  showPopupModal(component, payload) {
    return function (dispatch) {
      const componentWithData = {
        component,
        data: payload,
      };

      dispatch({
        type: constants.SHOW_POPUP_MODAL,
        payload: componentWithData,
      });
    };
  },
  hidePopupModal(payload) {
    return function (dispatch) {
      dispatch({
        type: constants.HIDE_POPUP_MODAL,
        payload,
      });
    };
  },
  activateHamburger() {
    return function (dispatch) {
      dispatch({
        type: constants.HAMBURGER_ACTIVE,
      });
    };
  },
  deactivateHamburger() {
    return function (dispatch) {
      dispatch({
        type: constants.HAMBURGER_INACTIVE,
      });
    };
  },
  toggleOverlay(type, bool) {
    return function (dispatch) {
      dispatch({
        type: constants.TOGGLE_OVERLAY,
        payload: {
          type,
          bool,
        },
      });
    };
  },
  changeMapOverlay(mapOverlayKey) {
    // revert map overlay if specified overlay key doesn't exist
    const finalOverlayKey = mapConstants.OVERLAYS.includes(mapOverlayKey) ? mapOverlayKey : mapConstants.NO_OVERLAY;

    return function (dispatch) {
      dispatch({
        type: constants.MAP_OVERLAY_CHANGED,
        payload: finalOverlayKey,
      });
    };
  },
  changeMapType(mapType) {
    const finalMapType = mapConstants.MAP_TYPES.includes(mapType) ? mapType : mapConstants.DEFAULT;

    return function (dispatch) {
      dispatch({
        type: constants.MAP_TYPE_CHANGED,
        payload: finalMapType,
      });
    };
  },
  resetMap() {
    return function (dispatch) {
      dispatch({
        type: constants.MAP_TYPE_CHANGED,
        payload: mapConstants.DEFAULT,
      });
      dispatch({
        type: constants.MAP_OVERLAY_CHANGED,
        payload: mapConstants.NO_OVERLAY,
      });
    };
  },
  updateMapBounds(mapBounds) {
    return function (dispatch) {
      dispatch({
        type: constants.UPDATE_MAP_BOUNDS,
        payload: {
          mapBounds,
        },
      });
    };
  },
  reportReview({ reviewId, comment, reviewType }) {
    return function (dispatch, getState) {
      const userToken = getState().user.userToken;
      return dispatch(api.review.report({ reviewId, comment, userToken, reviewType }));
    };
  },
  voteReview({ reviewId, vote, reviewType }) {
    return function (dispatch, getState) {
      const userToken = getState().user.userToken;
      return dispatch(api.review.vote({ reviewId, vote, userToken, reviewType }));
    };
  },
  saveReviewResponse(reviewId, comment) {
    return function (dispatch, getState) {
      const userEmail = getState().user.info.email;
      const date = new Date(Date.now());
      const dateISOString = date.toISOString();
      return dispatch(api.review.saveResponse(reviewId, comment, userEmail, dateISOString));
    };
  },
  setAppStoreBool(name, bool) {
    return function (dispatch) {
      return dispatch({
        type: constants.SET_APP_STORE_BOOL,
        payload: {
          name,
          bool,
        },
      });
    };
  },
  // HPWEB-5795 experimental sort order
  setBBTestGroup(bbTestGroup) {
    return function (dispatch, getState) {
      if (bbTestGroup) {
        dispatch({
          type: constants.SET_BOUNTY_BRAIN_TEST_GROUP,
          payload: {
            bbTestGroup,
          },
        });
      }
    };
  },
  resetBBTestGroup() {
    return function (dispatch) {
      dispatch({
        type: constants.SET_BOUNTY_BRAIN_TEST_GROUP,
        payload: {
          bbTestGroup: null,
        },
      });
      return Promise.resolve(true);
    };
  },
  setForceAdsRefresh(bool) {
    return function (dispatch) {
      dispatch({
        type: constants.SET_FORCE_ADS_REFRESH,
        payload: { bool },
      });
    };
  },
  setDevice(device = {}) {
    return (dispatch) => {
      dispatch({
        type: constants.SET_DEVICE,
        payload: device,
      });
    };
  },
  getClusterSettings() {
    return (dispatch) => {
      dispatch(api.clusterSettings()).then((response) => {
        if (response.success && response.data) {
          clusterSettingCache.reload(response.data);

          return dispatch({
            type: constants.UPDATE_CLUSTER_SETTINGS,
            payload: clusterSettingCache.clusterSettingDict,
          });
        }
      });
    };
  },
  getIp() {
    return (dispatch) => dispatch(api.user.ip());
  },
  setIpFromJava(response) {
    return (dispatch) =>
      dispatch({
        type: constants.UPDATE_IP_FROM_JAVA,
        payload: response,
      });
  },
  getGeoIp({ ip, trace = null }) {
    return (dispatch) => {
      trace?.startAsyncSpan({ marker: 'geoIp' });
      return dispatch(api.area.byIp(ip)).then((byIp) => {
        const { status } = byIp;
        const areaData = byIp.data;
        if (status !== 'OK') {
          logger?.warn({ byIp }, 'No geocode for IP %s', ip);
          trace?.finishAsyncSpan({ marker: 'geoIp' });
          return false;
        }
        const { type } = byIp.data;
        if (type) {
          trace?.finishAsyncSpan({ marker: 'geoIp' });
          return dispatch({
            type: constants.GEOLOCATION_USER_AREA,
            area: new Area(areaData),
          });
        } else {
          logger?.warn({ byIp }, 'No area for geocoded IP %s', ip);
          trace?.finishAsyncSpan({ marker: 'geoIp' });
          return false;
        }
      });
    };
  },
};

export default AppActions;
